import { useState } from "react"
import { NavLink, Outlet } from "react-router-dom"
import { useAuth } from "../AuthContext"
import { LogOut, User, Calendar, Receipt, Menu, X } from "lucide-react"

const DashboardLayout = () => {
  const { currentUser, logout } = useAuth()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const clientInfo = currentUser || { name: "Client", email: "..." }

  const navigation = [
    { name: "Profile", path: "/", icon: User },
    { name: "Bookings", path: "/bookings", icon: Calendar },
    { name: "Invoices", path: "/invoices", icon: Receipt },
  ]

  return (
    <div className="min-h-screen flex bg-gray-50 font-inter">
      
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-gray-200 h-16 flex items-center justify-between px-4 shadow-sm">
          <span className="text-xl font-extrabold text-indigo-700 tracking-wider">
            CCACC Rental
          </span>
          <button 
            onClick={() => setIsMobileMenuOpen(true)} 
            className="p-2 -mr-2 text-gray-600 hover:bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <Menu className="w-6 h-6" />
          </button>
      </div>

      {/* Mobile Overlay Backdrop */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm transition-opacity"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Left Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 shadow-xl 
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0 lg:static lg:inset-auto lg:flex lg:flex-col
        ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"}
      `}>
        {/* Logo/Title Area */}
        <div className="h-20 lg:h-20 flex items-center justify-between lg:justify-center border-b border-gray-100 px-4">
          <span className="text-2xl font-extrabold text-indigo-700 tracking-wider">
            CCACC Rental
          </span>
          {/* Close Button - Mobile Only */}
          <button 
            onClick={() => setIsMobileMenuOpen(false)} 
            className="lg:hidden p-2 -mr-2 text-gray-500 hover:bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 flex items-center justify-center bg-indigo-500 text-white font-bold text-lg rounded-full">
              {clientInfo.name?.charAt(0) || clientInfo.email?.charAt(0) || "C"}
            </div>
            <div className="overflow-hidden">
              <p className="font-semibold text-gray-800 text-sm truncate">
                {clientInfo.name || "Rental Client"}
              </p>
              <p className="text-xs text-gray-500 truncate">{clientInfo.email}</p>
            </div>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {navigation.map(item => (
            <NavLink
              key={item.name}
              to={item.path}
              end={item.path === "/"}
              onClick={() => setIsMobileMenuOpen(false)} // Close sidebar on mobile when clicked
              className={({ isActive }) =>
                `w-full flex items-center space-x-3 p-3 rounded-xl transition duration-150 text-left ${
                  isActive
                    ? "bg-indigo-100 text-indigo-700 font-semibold shadow-md"
                    : "text-gray-600 hover:bg-gray-100 hover:text-indigo-600"
                }`
              }
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              <span>{item.name}</span>
            </NavLink>
          ))}
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-gray-100">
          <button
            onClick={logout}
            className="w-full flex items-center justify-center space-x-3 p-3 bg-red-50 text-red-600 font-semibold rounded-xl hover:bg-red-100 transition duration-150"
          >
            <LogOut className="w-5 h-5" />
            <span>Log Out</span>
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <main className="flex-1 overflow-y-auto p-4 sm:p-8 pt-20 lg:pt-8">
        <Outlet />
      </main>
    </div>
  )
}

export default DashboardLayout
